import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';

export type Pull = Tables<'pulls'> & {
  profile_username?: string;
  profile_emoji?: string;
  like_count?: number;
  user_liked?: boolean;
};

export function usePulls() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['pulls'],
    queryFn: async () => {
      const { data: pulls, error } = await supabase
        .from('pulls')
        .select('id, user_id, image_url, caption, created_at')
        .order('created_at', { ascending: false })
        .limit(60);
      if (error) throw error;
      if (!pulls?.length) return [];

      const userIds = [...new Set(pulls.map(p => p.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, username, avatar_emoji')
        .in('user_id', userIds);
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));

      const ids = pulls.map(p => p.id);
      const { data: likes } = await supabase
        .from('pull_likes')
        .select('pull_id, user_id')
        .in('pull_id', ids);
      const likeCount = new Map<string, number>();
      const userLiked = new Set<string>();
      (likes || []).forEach(l => {
        likeCount.set(l.pull_id, (likeCount.get(l.pull_id) || 0) + 1);
        if (user && l.user_id === user.id) userLiked.add(l.pull_id);
      });

      return pulls.map(p => ({
        ...p,
        profile_username: profileMap.get(p.user_id)?.username || 'Unknown',
        profile_emoji: profileMap.get(p.user_id)?.avatar_emoji || '🧙',
        like_count: likeCount.get(p.id) || 0,
        user_liked: userLiked.has(p.id),
      })) as Pull[];
    },
  });
}

export function useCreatePull() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ imageUrl, caption }: { imageUrl: string; caption: string }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('pulls').insert({
        user_id: user.id,
        image_url: imageUrl,
        caption: caption || null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pulls'] }),
  });
}

export function useTogglePullLike() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pullId, liked }: { pullId: string; liked: boolean }) => {
      if (!user) throw new Error('Not authenticated');
      if (liked) {
        await supabase.from('pull_likes').delete().eq('pull_id', pullId).eq('user_id', user.id);
      } else {
        await supabase.from('pull_likes').insert({ pull_id: pullId, user_id: user.id });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pulls'] }),
  });
}
